import { useQuery } from '@tanstack/react-query'
import { useContext, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import { getAffectedStaff } from '../../api/affected-staffs/get-affected-staff'
import { AffectedStaffContext } from '../contexts/affected-staff'
import type { AffectedStaffMap } from '../types/models/affectedStaff/affected-staff'

type UseAffectedStaffResult = {
  affectedStaff: AffectedStaffMap['FULL'] | null
  setAffectedStaff: (s: AffectedStaffMap['FULL'] | null) => void
  isLoading: boolean
  refetch: () => Promise<AffectedStaffMap['FULL'] | undefined>
}

export default function useAffectedStaff(): UseAffectedStaffResult {
  const { affectedStaff, setAffectedStaff } = useContext(AffectedStaffContext)
  const { id } = useParams<{ id?: string }>()

  const query = useQuery({
    queryKey: ['affected-staff', id],
    queryFn: () => {
      if (!id) return undefined
      return getAffectedStaff(Number(id), 'FULL')
    },
    enabled: !!id && !affectedStaff,
    staleTime: 1000 * 60 // 1 minute
  })

  useEffect(() => {
    if (query.data && setAffectedStaff) setAffectedStaff(query.data)
  }, [query.data, setAffectedStaff])

  return {
    affectedStaff: affectedStaff ?? query.data ?? null,
    setAffectedStaff,
    isLoading: query.isFetching || query.isLoading,
    refetch: async () => {
      const r = await query.refetch()
      return r.data
    }
  }
}
